import { SettingUrls } from 'constants/SettingsUrls';
import { BannerKey, BannerPosition } from 'features/banner/model/banner';
import useSendGTMAction from 'features/google-tag-manager/hooks/useSendGTMAction';
import { BannerActionClickedGtmAction } from 'features/google-tag-manager/model/actions/banner_action_clicked.gtm-action';
import { useAppStatusSWR } from 'features/settings/components/ppoints/dashboard/app-status/hooks/useAppStatusSWR';
import useProviderLimitExceeded from 'hooks/plan/useProviderLimitExceeded';
import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { AppStatus } from '../../../../pb/settings/v1/app_status_pb';
import { ProviderLimitExceededModal } from './ProviderLimitExceededModal';

interface ProviderLimitExceededBannerProps {
    /** Where the banner is rendered, reported with its GTM click events */
    position: BannerPosition;
}

/**
 * Tells the merchant the app is inactive because more carriers are active than the current plan allows.
 * Self-guards on that check and on the app really being inactive, so callers can render it unconditionally.
 */
export const ProviderLimitExceededBanner: React.FC<ProviderLimitExceededBannerProps> = ({ position }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const sendGTMAction = useSendGTMAction();
    const modalId = useId();
    const { isLimitExceeded, limit, planName } = useProviderLimitExceeded();
    const { data: appStatus, isLoading: isAppStatusLoading } = useAppStatusSWR();

    if (!isLimitExceeded || isAppStatusLoading || appStatus?.appStatus === AppStatus.ACTIVE) {
        return null;
    }

    const handleUpgrade = () => {
        sendGTMAction(
            new BannerActionClickedGtmAction({
                name: 'upgrade_plan',
                key: BannerKey.PROVIDER_LIMIT_EXCEEDED,
                position,
            })
        );
        navigate(SettingUrls.billing);
    };

    return (
        <>
            <s-banner
                tone="warning"
                heading={t('PROVIDER_LIMIT_EXCEEDED_BANNER.HEADING', { count: limit, plan: planName })}
            >
                <div>{t('PROVIDER_LIMIT_EXCEEDED_BANNER.DESCRIPTION', { count: limit })}</div>
                <div className="flex gap-2 mt-2">
                    <s-button variant="primary" commandFor={modalId} command="--show">
                        {t('PROVIDER_LIMIT_EXCEEDED_BANNER.CHOOSE_CARRIER', { count: limit })}
                    </s-button>
                    <s-button onClick={handleUpgrade}>{t('PROVIDER_LIMIT_EXCEEDED_BANNER.UPGRADE_PLAN')}</s-button>
                </div>
            </s-banner>
            <ProviderLimitExceededModal id={modalId} limit={limit} planName={planName} />
        </>
    );
};
